import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Card,
  CardContent,
  Stack,
  alpha
} from '@mui/material';
import { 
  Settings as SettingsIcon,
  Person as PersonIcon,
  RecentActors as InvestmentProfilesIcon,
  Payment as SubscriptionIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { SubscriptionManager } from '../auth';
import { InvestmentProfileManager } from '../investment/InvestmentProfileManager';
import { CreateProfileDialog } from '../investment/CreateProfileDialog';
import { clearCache } from '../../utils/memoryCache';
import { useTheme } from '@mui/material/styles';

interface UserProfilePageProps {
  user?: {
    userName?: string;  // This will be their login email/username
    email?: string;  // This will be their login email/username
    role?: string;
    provider?: string;  // 'google' or 'x' (twitter)
  };
  onLogout: () => void;
  urlPrimaryBase: string;  // Make it required
  onProfilesChange?: () => void;  // Add this prop
  onPlanChange?: (newRole: string) => void;
}

type SubPage = 'user' | 'investment' | 'subscription';

// Add console.log to verify the import is working
console.log('InvestmentProfileManager imported:', InvestmentProfileManager);

const UserProfilePage: React.FC<UserProfilePageProps> = ({ user, onLogout, onProfilesChange, onPlanChange }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [activePage, setActivePage] = useState<SubPage>('user');
  const profileManagerRef = useRef<HTMLDivElement>(null);
  const [selectedProfile, setSelectedProfile] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const investmentManagerRef = useRef<{ refreshProfiles: () => void }>(null);
  const [currentRole, setCurrentRole] = useState(user?.role);

  useEffect(() => {
    if (location.state?.defaultTab === 'investment') {
      setActivePage('investment');
    } else if (location.state?.defaultTab === 'subscription') {
      setActivePage('subscription');
    }
  }, [location.state]);

  const handleClose = () => {
    navigate(-1);
  };

  const formatRole = (role?: string) => {
    if (!role) return 'Unknown';
    return role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ');
  };

  const formatProvider = (provider?: string) => {
    if (!provider) return 'N/A';
    return provider.charAt(0).toUpperCase() + provider.slice(1);
  };

  const handleLogout = () => {
    onLogout();
    navigate('/', { replace: true });
  };

  const renderContent = () => {
    switch (activePage) {
      case 'user':
        return (
          <Card sx={{ 
            height: '100%',
            borderRadius: 2,
            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                User Information
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Login</Typography>
                  <Typography variant="body1">{user?.userName || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Current Plan</Typography>
                  <Typography variant="body1">{formatRole(currentRole)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        );
      
      case 'investment':
        return (
          <Card sx={{ 
            height: '100%',
            borderRadius: 2,
            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Investment Profiles
              </Typography>
              {(currentRole === 'premium' || currentRole === 'premium_plus') ? (
                <Box sx={{ mt: 2 }}>
                  <InvestmentProfileManager 
                    ref={investmentManagerRef}
                    onTickerSelect={(ticker: string) => navigate(`/market/stocks/${ticker}`)}
                    onCreateClick={() => setShowCreateDialog(true)}
                    onProfilesChange={onProfilesChange}
                  />
                </Box>
              ) : (
                <Box 
                  sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Premium Feature
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Investment Profiles are only available to Premium subscribers.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Upgrade your subscription to get access to Investment Profiles and more premium features.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setActivePage('subscription')}
                    sx={{ mt: 2 }}
                  >
                    Upgrade Subscription
                  </Button>
                </Box>
              )}

              <CreateProfileDialog
                open={showCreateDialog}
                onClose={() => setShowCreateDialog(false)}
                onProfileCreated={(newProfile) => {
                  setShowCreateDialog(false);
                  setTimeout(() => {
                    investmentManagerRef.current?.refreshProfiles?.();
                    onProfilesChange?.();
                  }, 100);
                }}
              />
            </CardContent>
          </Card>
        );
      
      case 'subscription':
        return (
          <Card sx={{ 
            height: '100%',
            borderRadius: 2,
            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Manage Subscription
              </Typography>
              <Box sx={{ 
                '& .MuiDialog-root': {
                  position: 'relative',
                  '& .MuiDialogActions-root': {
                    display: 'none'
                  }
                }
              }}>
                <SubscriptionManager
                  onClose={() => {}}
                  isEmbedded={true}
                  onPlanChange={(newRole) => {
                    clearCache();
                    setCurrentRole(newRole);
                    onPlanChange?.(newRole);
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton 
          onClick={handleClose}
          sx={{ 
            color: theme.palette.custom.iconSecondary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1)
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          User Profile
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Left Navigation Panel */}
        <Grid item xs={12} md={3}>
          <Paper 
            sx={{ 
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 2
            }}
          >
            <List component="nav">
              <ListItemButton 
                selected={activePage === 'user'}
                onClick={() => setActivePage('user')}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15)
                    }
                  }
                }}
              >
                <ListItemIcon>
                  <PersonIcon color={activePage === 'user' ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText primary="User Settings" />
              </ListItemButton>

              <ListItemButton
                selected={activePage === 'investment'}
                onClick={() => setActivePage('investment')}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15)
                    }
                  }
                }}
              >
                <ListItemIcon>
                  <InvestmentProfilesIcon color={activePage === 'investment' ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText primary="Investment Profile Settings" />
              </ListItemButton>

              <ListItemButton
                selected={activePage === 'subscription'}
                onClick={() => setActivePage('subscription')}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15)
                    }
                  }
                }}
              >
                <ListItemIcon>
                  <SubscriptionIcon color={activePage === 'subscription' ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText primary="Subscription Settings" />
              </ListItemButton>

              <Divider sx={{ my: 2 }} />

              <ListItemButton 
                onClick={handleLogout}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.1)
                  }
                }}
              >
                <ListItemIcon>
                  <LogoutIcon color="error" />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </List>
          </Paper>
        </Grid>

        {/* Main Content Area */}
        <Grid item xs={12} md={9}>
          {renderContent()}
        </Grid>
      </Grid>
    </Container>
  );
};

export default UserProfilePage; 